import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import SortRoundedIcon from "@mui/icons-material/SortRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import axios from "axios";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import config from "../../Config";

const COLORS = {
  cream: "#F7F4EF",
  softCream: "#FBF9F6",
  white: "#FFFFFF",
  black: "#171717",
  mutedBlack: "#3A3A3A",
  grey: "#77736D",
  lightGrey: "#A29D95",
  border: "#DDD7CE",
  gold: "#977743",
  goldLight: "#C4A36A",
};

const PRODUCTS_PER_PAGE = 12;

export default function AllProduct() {
  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const initialSearch = queryParams.get("search") || "";

  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState(initialSearch);
  const [size, setSize] = useState("");
  const [price, setPrice] = useState("");
  const [availability, setAvailability] = useState("");
  const [security, setSecurity] = useState("");
  const [city, setCity] = useState("");
  const [sort, setSort] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const response = await axios.get(
        `${config.apiBaseUrl}/${config.allProducts}`,
      );

      setProducts(response.data?.Products || []);
    } catch (err) {
      console.error("Error fetching products:", err);
      setProducts([]);
      setError("Unable to load the collection right now.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    setSearch(initialSearch);
  }, [initialSearch]);

  const cities = useMemo(() => {
    return [
      ...new Set(
        products
          .map((item) => item?.address?.city)
          .filter((value) => value && value.trim()),
      ),
    ].sort((a, b) => a.localeCompare(b));
  }, [products]);

  const filteredProducts = useMemo(() => {
    let result = [...products];

    const searchValue = search.trim().toLowerCase();

    if (searchValue) {
      result = result.filter((item) => {
        const product = item?.product || {};
        const address = item?.address || {};

        const searchableText = [
          product.title,
          product.description,
          product.size,
          address.city,
          address.state,
          address.country,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return searchableText.includes(searchValue);
      });
    }

    if (size) {
      result = result.filter(
        (item) => item?.product?.size?.toLowerCase() === size.toLowerCase(),
      );
    }

    if (price) {
      result = result.filter((item) => {
        const rentAmount = Number(item?.product?.rentAmount || 0);

        if (price === "under1000") {
          return rentAmount < 1000;
        }

        if (price === "1000to2000") {
          return rentAmount >= 1000 && rentAmount <= 2000;
        }

        if (price === "2000to4000") {
          return rentAmount > 2000 && rentAmount <= 4000;
        }

        if (price === "above4000") {
          return rentAmount > 4000;
        }

        return true;
      });
    }

    if (availability) {
      result = result.filter((item) => {
        if (availability === "available") {
          return item?.productStatus !== false;
        }

        if (availability === "rented") {
          return item?.productStatus === false;
        }

        return true;
      });
    }

    if (security) {
      result = result.filter((item) => {
        const isSecurity = Boolean(item?.product?.isSecurity);

        if (security === "required") {
          return isSecurity;
        }

        if (security === "notRequired") {
          return !isSecurity;
        }

        return true;
      });
    }

    if (city) {
      result = result.filter(
        (item) => item?.address?.city?.toLowerCase() === city.toLowerCase(),
      );
    }

    if (sort === "priceLow") {
      result.sort(
        (a, b) =>
          Number(a?.product?.rentAmount || 0) -
          Number(b?.product?.rentAmount || 0),
      );
    }

    if (sort === "priceHigh") {
      result.sort(
        (a, b) =>
          Number(b?.product?.rentAmount || 0) -
          Number(a?.product?.rentAmount || 0),
      );
    }

    if (sort === "newest") {
      result.sort(
        (a, b) =>
          new Date(b?.createdAt || 0).getTime() -
          new Date(a?.createdAt || 0).getTime(),
      );
    }

    return result;
  }, [products, search, size, price, availability, security, city, sort]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE),
  );

  const paginatedProducts = useMemo(() => {
    const startIndex = (page - 1) * PRODUCTS_PER_PAGE;

    return filteredProducts.slice(startIndex, startIndex + PRODUCTS_PER_PAGE);
  }, [filteredProducts, page]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  useEffect(() => {
    setPage(1);
  }, [search, size, price, availability, security, city, sort]);

  const hasFilters =
    Boolean(search) ||
    Boolean(size) ||
    Boolean(price) ||
    Boolean(availability) ||
    Boolean(security) ||
    Boolean(city);

  const clearFilters = () => {
    setSearch("");
    setSize("");
    setPrice("");
    setAvailability("");
    setSecurity("");
    setCity("");
    setSort("");

    navigate("/AllProduct", {
      replace: true,
    });
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
  };

  const handleProductClick = (productId) => {
    if (!productId) return;

    navigate(`/productdetail/${productId}`);
  };

  const getAvailabilityLabel = (item) => {
    return item?.productStatus === false ? "Rented" : "Available";
  };

  return (
    <>
      <Navbar />

      <Box
        sx={{
          backgroundColor: COLORS.cream,
          minHeight: "70vh",
          py: { xs: 4, sm: 5, md: 7 },
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 1440,
            mx: "auto",
            px: { xs: 2, sm: 3, md: 5 },
          }}
        >
          <Box
            sx={{
              mb: { xs: 4, md: 5 },
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
              gap: 3,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ flex: 1, minWidth: 280 }}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mb: 1.5 }}
              >
                <SearchRoundedIcon
                  sx={{
                    fontSize: 18,
                    color: COLORS.gold,
                  }}
                />

                <Typography
                  sx={{
                    fontSize: 10,
                    letterSpacing: "0.2em",
                    textTransform: "uppercase",
                    color: COLORS.gold,
                    fontWeight: 700,
                  }}
                >
                  Hindustan Wardrobe
                </Typography>
              </Stack>

              <Typography
                component="h1"
                sx={{
                  fontFamily: "Georgia, serif",
                  fontSize: {
                    xs: 34,
                    sm: 44,
                    md: 54,
                  },
                  fontWeight: 400,
                  lineHeight: 1.05,
                  color: COLORS.black,
                  mb: 1.5,
                }}
              >
                The Collection
              </Typography>

              <Typography
                sx={{
                  color: COLORS.grey,
                  fontSize: 14,
                  lineHeight: 1.8,
                  maxWidth: 620,
                }}
              >
                Discover fashion pieces curated for celebrations, occasions and
                moments worth remembering.
              </Typography>
            </Box>

            <Box
              sx={{
                width: { xs: "100%", sm: 170 },
                border: `1px solid ${COLORS.border}`,
                backgroundColor: COLORS.softCream,
                px: 2.5,
                py: 2,
              }}
            >
              <Typography
                sx={{
                  fontSize: 9,
                  letterSpacing: "0.17em",
                  textTransform: "uppercase",
                  color: COLORS.lightGrey,
                  mb: 0.5,
                }}
              >
                Pieces
              </Typography>

              <Typography
                sx={{
                  fontFamily: "Georgia, serif",
                  fontSize: 28,
                  color: COLORS.black,
                }}
              >
                {filteredProducts.length}
              </Typography>
            </Box>
          </Box>

          <Card
            elevation={0}
            sx={{
              border: `1px solid ${COLORS.border}`,
              borderRadius: 0,
              backgroundColor: COLORS.white,
              mb: 5,
            }}
          >
            <Box
              sx={{
                px: { xs: 2, sm: 3 },
                py: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Stack direction="row" spacing={1.2} alignItems="center">
                <TuneRoundedIcon
                  sx={{
                    color: COLORS.gold,
                    fontSize: 19,
                  }}
                />

                <Typography
                  sx={{
                    fontSize: 10,
                    letterSpacing: "0.17em",
                    textTransform: "uppercase",
                    fontWeight: 700,
                    color: COLORS.black,
                  }}
                >
                  Refine Collection
                </Typography>
              </Stack>

              {hasFilters && (
                <Box
                  onClick={clearFilters}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    cursor: "pointer",
                    color: COLORS.gold,
                    "&:hover": {
                      color: COLORS.black,
                    },
                  }}
                >
                  <CloseRoundedIcon sx={{ fontSize: 15 }} />

                  <Typography
                    sx={{
                      fontSize: 10,
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                      fontWeight: 700,
                    }}
                  >
                    Clear Filters
                  </Typography>
                </Box>
              )}
            </Box>

            <Divider
              sx={{
                borderColor: COLORS.border,
              }}
            />

            <Box
              sx={{
                p: { xs: 2, sm: 3 },
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(4, 1fr)",
                },
                gap: 2,
              }}
            >
              <FormControl fullWidth size="small">
                <InputLabel
                  sx={{
                    "&.Mui-focused": {
                      color: COLORS.gold,
                    },
                  }}
                >
                  Size
                </InputLabel>

                <Select
                  value={size}
                  label="Size"
                  onChange={(e) => setSize(e.target.value)}
                  sx={{
                    borderRadius: 0,
                    backgroundColor: COLORS.softCream,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: COLORS.border,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: COLORS.gold,
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: COLORS.gold,
                    },
                  }}
                >
                  <MenuItem value="">All Sizes</MenuItem>
                  <MenuItem value="XS">XS</MenuItem>
                  <MenuItem value="S">S</MenuItem>
                  <MenuItem value="M">M</MenuItem>
                  <MenuItem value="L">L</MenuItem>
                  <MenuItem value="XL">XL</MenuItem>
                  <MenuItem value="XXL">XXL</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel
                  sx={{
                    "&.Mui-focused": {
                      color: COLORS.gold,
                    },
                  }}
                >
                  Price
                </InputLabel>

                <Select
                  value={price}
                  label="Price"
                  onChange={(e) => setPrice(e.target.value)}
                  sx={{
                    borderRadius: 0,
                    backgroundColor: COLORS.softCream,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: COLORS.border,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: COLORS.gold,
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: COLORS.gold,
                    },
                  }}
                >
                  <MenuItem value="">Any Price</MenuItem>
                  <MenuItem value="under1000">Under ₹1,000</MenuItem>
                  <MenuItem value="1000to2000">₹1,000 – ₹2,000</MenuItem>
                  <MenuItem value="2000to4000">₹2,000 – ₹4,000</MenuItem>
                  <MenuItem value="above4000">₹4,000+</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel
                  sx={{
                    "&.Mui-focused": {
                      color: COLORS.gold,
                    },
                  }}
                >
                  Availability
                </InputLabel>

                <Select
                  value={availability}
                  label="Availability"
                  onChange={(e) => setAvailability(e.target.value)}
                  sx={{
                    borderRadius: 0,
                    backgroundColor: COLORS.softCream,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: COLORS.border,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: COLORS.gold,
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: COLORS.gold,
                    },
                  }}
                >
                  <MenuItem value="">Any Status</MenuItem>
                  <MenuItem value="available">Available</MenuItem>
                  <MenuItem value="rented">Rented</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel
                  sx={{
                    "&.Mui-focused": {
                      color: COLORS.gold,
                    },
                  }}
                >
                  Security
                </InputLabel>

                <Select
                  value={security}
                  label="Security"
                  onChange={(e) => setSecurity(e.target.value)}
                  sx={{
                    borderRadius: 0,
                    backgroundColor: COLORS.softCream,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: COLORS.border,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: COLORS.gold,
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: COLORS.gold,
                    },
                  }}
                >
                  <MenuItem value="">Any Security</MenuItem>
                  <MenuItem value="required">Security Required</MenuItem>
                  <MenuItem value="notRequired">No Security</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel
                  sx={{
                    "&.Mui-focused": {
                      color: COLORS.gold,
                    },
                  }}
                >
                  City
                </InputLabel>

                <Select
                  value={city}
                  label="City"
                  onChange={(e) => setCity(e.target.value)}
                  sx={{
                    borderRadius: 0,
                    backgroundColor: COLORS.softCream,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: COLORS.border,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: COLORS.gold,
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: COLORS.gold,
                    },
                  }}
                >
                  <MenuItem value="">All Cities</MenuItem>

                  {cities.map((item) => (
                    <MenuItem key={item} value={item}>
                      {item}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl
                fullWidth
                size="small"
                sx={{
                  gridColumn: {
                    xs: "auto",
                    sm: "span 2",
                    md: "span 3",
                  },
                }}
              >
                <InputLabel
                  sx={{
                    "&.Mui-focused": {
                      color: COLORS.gold,
                    },
                  }}
                >
                  Search
                </InputLabel>

                <Select
                  value=""
                  displayEmpty
                  renderValue={() => search || "Search collection"}
                  sx={{
                    borderRadius: 0,
                    backgroundColor: COLORS.softCream,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: COLORS.border,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: COLORS.gold,
                    },
                  }}
                >
                  <MenuItem value="" sx={{ display: "none" }}>
                    Search
                  </MenuItem>
                </Select>

                <Box
                  component="input"
                  value={search}
                  onChange={handleSearchChange}
                  placeholder="Search by title, description, size or location"
                  sx={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    width: "100%",
                    height: "100%",
                    opacity: 0,
                    cursor: "text",
                  }}
                />
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel
                  sx={{
                    "&.Mui-focused": {
                      color: COLORS.gold,
                    },
                  }}
                >
                  Sort
                </InputLabel>

                <Select
                  value={sort}
                  label="Sort"
                  onChange={(e) => setSort(e.target.value)}
                  startAdornment={
                    <SortRoundedIcon
                      sx={{
                        mr: 0.8,
                        fontSize: 18,
                        color: COLORS.gold,
                      }}
                    />
                  }
                  sx={{
                    borderRadius: 0,
                    backgroundColor: COLORS.softCream,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: COLORS.border,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: COLORS.gold,
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: COLORS.gold,
                    },
                  }}
                >
                  <MenuItem value="">Recommended</MenuItem>
                  <MenuItem value="newest">Newest First</MenuItem>
                  <MenuItem value="priceLow">Price: Low to High</MenuItem>
                  <MenuItem value="priceHigh">Price: High to Low</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {hasFilters && (
              <Box
                sx={{
                  px: { xs: 2, sm: 3 },
                  pb: 2.5,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                {search && (
                  <Chip
                    label={`Search: ${search}`}
                    onDelete={() => setSearch("")}
                    sx={{
                      borderRadius: 0,
                      backgroundColor: COLORS.cream,
                      color: COLORS.black,
                    }}
                  />
                )}

                {size && (
                  <Chip
                    label={`Size: ${size}`}
                    onDelete={() => setSize("")}
                    sx={{
                      borderRadius: 0,
                      backgroundColor: COLORS.cream,
                      color: COLORS.black,
                    }}
                  />
                )}

                {price && (
                  <Chip
                    label="Price"
                    onDelete={() => setPrice("")}
                    sx={{
                      borderRadius: 0,
                      backgroundColor: COLORS.cream,
                      color: COLORS.black,
                    }}
                  />
                )}

                {availability && (
                  <Chip
                    label={
                      availability === "available" ? "Available" : "Rented"
                    }
                    onDelete={() => setAvailability("")}
                    sx={{
                      borderRadius: 0,
                      backgroundColor: COLORS.cream,
                      color: COLORS.black,
                    }}
                  />
                )}

                {security && (
                  <Chip
                    label={
                      security === "required"
                        ? "Security Required"
                        : "No Security"
                    }
                    onDelete={() => setSecurity("")}
                    sx={{
                      borderRadius: 0,
                      backgroundColor: COLORS.cream,
                      color: COLORS.black,
                    }}
                  />
                )}

                {city && (
                  <Chip
                    label={city}
                    onDelete={() => setCity("")}
                    sx={{
                      borderRadius: 0,
                      backgroundColor: COLORS.cream,
                      color: COLORS.black,
                    }}
                  />
                )}
              </Box>
            )}
          </Card>

          {loading ? (
            <Box
              sx={{
                minHeight: 400,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <CircularProgress
                size={30}
                thickness={2}
                sx={{
                  color: COLORS.gold,
                }}
              />

              <Typography
                sx={{
                  fontSize: 10,
                  letterSpacing: "0.17em",
                  textTransform: "uppercase",
                  color: COLORS.grey,
                }}
              >
                Curating the collection
              </Typography>
            </Box>
          ) : error ? (
            <Box
              sx={{
                minHeight: 400,
                border: `1px solid ${COLORS.border}`,
                backgroundColor: COLORS.softCream,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                px: 3,
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontFamily: "Georgia, serif",
                    fontSize: 30,
                    color: COLORS.black,
                    mb: 1,
                  }}
                >
                  Collection unavailable
                </Typography>

                <Typography
                  sx={{
                    color: COLORS.grey,
                    fontSize: 14,
                    mb: 3,
                  }}
                >
                  {error}
                </Typography>

                <Box
                  component="button"
                  onClick={fetchProducts}
                  sx={{
                    border: 0,
                    backgroundColor: COLORS.black,
                    color: COLORS.white,
                    px: 3,
                    py: 1.5,
                    fontSize: 10,
                    letterSpacing: "0.15em",
                    textTransform: "uppercase",
                    fontWeight: 700,
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: COLORS.gold,
                    },
                  }}
                >
                  Try Again
                </Box>
              </Box>
            </Box>
          ) : filteredProducts.length === 0 ? (
            <Box
              sx={{
                minHeight: 400,
                border: `1px solid ${COLORS.border}`,
                backgroundColor: COLORS.softCream,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                px: 3,
              }}
            >
              <Box>
                <Box
                  sx={{
                    width: 62,
                    height: 62,
                    mx: "auto",
                    mb: 2.5,
                    border: `1px solid ${COLORS.border}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <SearchRoundedIcon
                    sx={{
                      fontSize: 26,
                      color: COLORS.gold,
                    }}
                  />
                </Box>

                <Typography
                  sx={{
                    fontFamily: "Georgia, serif",
                    fontSize: 30,
                    color: COLORS.black,
                    mb: 1,
                  }}
                >
                  No pieces found
                </Typography>

                <Typography
                  sx={{
                    color: COLORS.grey,
                    fontSize: 14,
                    lineHeight: 1.8,
                    maxWidth: 450,
                    mx: "auto",
                    mb: 3,
                  }}
                >
                  Try adjusting your filters or explore the complete collection
                  again.
                </Typography>

                {hasFilters && (
                  <Box
                    component="button"
                    onClick={clearFilters}
                    sx={{
                      border: 0,
                      backgroundColor: COLORS.black,
                      color: COLORS.white,
                      px: 3,
                      py: 1.5,
                      fontSize: 10,
                      letterSpacing: "0.15em",
                      textTransform: "uppercase",
                      fontWeight: 700,
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: COLORS.gold,
                      },
                    }}
                  >
                    Reset Collection
                  </Box>
                )}
              </Box>
            </Box>
          ) : (
            <>
              <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
                {paginatedProducts.map((item) => {
                  const product = item?.product || {};
                  const image =
                    item?.productImage?.[0] ||
                    "/placeholder.svg?height=400&width=300";

                  const isAvailable = item?.productStatus !== false;

                  return (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      lg={3}
                      key={item?._id || product.productId}
                    >
                      <Card
                        elevation={0}
                        onClick={() => handleProductClick(product.productId)}
                        sx={{
                          height: "100%",
                          borderRadius: 0,
                          border: `1px solid ${COLORS.border}`,
                          backgroundColor: COLORS.white,
                          cursor: "pointer",
                          overflow: "hidden",
                          transition:
                            "transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease",
                          "&:hover": {
                            transform: "translateY(-5px)",
                            borderColor: COLORS.goldLight,
                            boxShadow: "0 18px 45px rgba(23,23,23,0.09)",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            position: "relative",
                            overflow: "hidden",
                            backgroundColor: COLORS.cream,
                          }}
                        >
                          <Box
                            component="img"
                            src={image}
                            alt={product.title || "Hindustan Wardrobe product"}
                            sx={{
                              display: "block",
                              width: "100%",
                              height: {
                                xs: 320,
                                sm: 300,
                                md: 320,
                              },
                              objectFit: "cover",
                              transition: "transform 0.55s ease",
                              "&:hover": {
                                transform: "scale(1.04)",
                              },
                            }}
                          />

                          <Box
                            sx={{
                              position: "absolute",
                              top: 12,
                              left: 12,
                              display: "flex",
                              gap: 0.7,
                              flexWrap: "wrap",
                            }}
                          >
                            <Chip
                              label={getAvailabilityLabel(item)}
                              size="small"
                              sx={{
                                height: 25,
                                borderRadius: 0,
                                backgroundColor: COLORS.white,
                                color: isAvailable ? COLORS.black : COLORS.grey,
                                border: `1px solid ${COLORS.border}`,
                                fontSize: 9,
                                letterSpacing: "0.1em",
                                textTransform: "uppercase",
                                fontWeight: 700,
                              }}
                            />
                          </Box>

                          {product.isSecurity && (
                            <Box
                              sx={{
                                position: "absolute",
                                top: 12,
                                right: 12,
                                width: 30,
                                height: 30,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: COLORS.white,
                                border: `1px solid ${COLORS.border}`,
                              }}
                            >
                              <SecurityOutlinedIcon
                                sx={{
                                  fontSize: 16,
                                  color: COLORS.gold,
                                }}
                              />
                            </Box>
                          )}
                        </Box>

                        <CardContent
                          sx={{
                            p: { xs: 2, sm: 2.4 },
                            "&:last-child": {
                              pb: { xs: 2, sm: 2.4 },
                            },
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: "Georgia, serif",
                              fontSize: 20,
                              lineHeight: 1.25,
                              color: COLORS.black,
                              mb: 0.8,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {product.title || "Untitled Collection"}
                          </Typography>

                          <Typography
                            sx={{
                              fontSize: 12,
                              color: COLORS.grey,
                              lineHeight: 1.6,
                              minHeight: 38,
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {product.description ||
                              "A carefully selected piece from our wardrobe."}
                          </Typography>

                          <Divider
                            sx={{
                              my: 1.8,
                              borderColor: COLORS.border,
                            }}
                          />

                          <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            gap={1}
                          >
                            <Box>
                              <Typography
                                sx={{
                                  fontSize: 15,
                                  fontWeight: 700,
                                  color: COLORS.black,
                                }}
                              >
                                ₹{product.rentAmount || 0}
                                <Typography
                                  component="span"
                                  sx={{
                                    fontSize: 10,
                                    color: COLORS.grey,
                                    ml: 0.5,
                                    fontWeight: 400,
                                  }}
                                >
                                  / day
                                </Typography>
                              </Typography>

                              {product.isSecurity &&
                                Number(product.securityAmount || 0) > 0 && (
                                  <Typography
                                    sx={{
                                      fontSize: 9,
                                      color: COLORS.grey,
                                      mt: 0.3,
                                    }}
                                  >
                                    ₹{product.securityAmount} security
                                  </Typography>
                                )}
                            </Box>

                            {item?.address?.city && (
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={0.3}
                                sx={{
                                  minWidth: 0,
                                  color: COLORS.grey,
                                }}
                              >
                                <LocationOnOutlinedIcon
                                  sx={{
                                    fontSize: 14,
                                    color: COLORS.gold,
                                  }}
                                />

                                <Typography
                                  sx={{
                                    fontSize: 10,
                                    maxWidth: 90,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {item.address.city}
                                </Typography>
                              </Stack>
                            )}
                          </Stack>

                          <Box
                            sx={{
                              mt: 2,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: 9,
                                letterSpacing: "0.14em",
                                textTransform: "uppercase",
                                color: COLORS.gold,
                                fontWeight: 700,
                              }}
                            >
                              View Piece
                            </Typography>

                            <Typography
                              sx={{
                                fontSize: 16,
                                color: COLORS.black,
                              }}
                            >
                              →
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>

              {totalPages > 1 && (
                <Box
                  sx={{
                    mt: { xs: 5, md: 7 },
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Pagination
                    page={page}
                    count={totalPages}
                    onChange={(_, value) => {
                      setPage(value);
                      window.scrollTo({
                        top: 0,
                        behavior: "smooth",
                      });
                    }}
                    shape="rounded"
                    sx={{
                      "& .MuiPaginationItem-root": {
                        borderRadius: 0,
                        color: COLORS.mutedBlack,
                        fontSize: 12,
                      },
                      "& .Mui-selected": {
                        backgroundColor: COLORS.black,
                        color: COLORS.white,
                      },
                      "& .Mui-selected:hover": {
                        backgroundColor: COLORS.gold,
                      },
                    }}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>

      <Footer />
    </>
  );
}
